// @ts-nocheck
/**
 * Teacher Notes Service
 * Date-based notes and classroom observations management for teachers.
 */

const STORAGE_PREFIX = 'academic_eye_teacher_notes_v1';

export const NOTE_CATEGORIES = [
  { value: 'Observation', label: 'Classroom Observation', color: '#2563eb' },
  { value: 'Progress', label: 'Student Progress', color: '#059669' },
  { value: 'Instruction', label: 'Instructional Reflection', color: '#7c3aed' },
  { value: 'Reminder', label: 'Reminder / Follow-up', color: '#d97706' },
  { value: 'General', label: 'General Note', color: '#4b5563' },
];

function getStorageKey(teacherId) {
  if (teacherId) return `${STORAGE_PREFIX}_${teacherId}`;
  return STORAGE_PREFIX;
}

// No sample or mock notes: only real teacher notes created by users
export function generateSampleNotesFromClasses() {
  return [];
}

function isSampleNote(n) {
  if (!n || !n.id) return true;
  const id = String(n.id);
  if (id.startsWith('note-sample-')) return true;
  return false;
}

function readStorage(teacherId) {
  const key = getStorageKey(teacherId);
  try {
    const raw = localStorage.getItem(key);
    let items = [];
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) items = parsed;
    }
    // Clean out any legacy sample notes
    const realItems = items.filter((n) => !isSampleNote(n));
    if (realItems.length !== items.length) {
      localStorage.setItem(key, JSON.stringify(realItems));
    }
    return realItems;
  } catch (err: any) {
    console.warn('[TeacherNotes] Could not read from localStorage:', err);
    return [];
  }
}

function writeStorage(items, teacherId) {
  const key = getStorageKey(teacherId);
  try {
    const realItems = (items || []).filter((n) => !isSampleNote(n));
    localStorage.setItem(key, JSON.stringify(realItems));
  } catch (err: any) {
    console.warn('[TeacherNotes] Could not write to localStorage:', err);
  }
}

/**
 * Fetch notes with search & filtering
 */
export async function fetchTeacherNotes({
  teacherId = null,
  teacherClasses = [],
  subject = 'all',
  classId = 'all',
  date = '',
  studentId = 'all',
  category = 'all',
  search = '',
} = {}) {
  const notes = readStorage(teacherId, teacherClasses);
  let filtered = [...notes];

  if (subject && subject !== 'all') {
    filtered = filtered.filter(
      (n) => (n.subject || '').toLowerCase() === subject.toLowerCase()
    );
  }

  if (classId && classId !== 'all') {
    filtered = filtered.filter(
      (n) => String(n.classId) === String(classId)
    );
  }

  if (date) {
    filtered = filtered.filter((n) => n.date === date);
  }

  if (studentId && studentId !== 'all') {
    filtered = filtered.filter((n) => String(n.studentId) === String(studentId));
  }

  if (category && category !== 'all') {
    filtered = filtered.filter((n) => n.category === category);
  }

  if (search && search.trim()) {
    const q = search.toLowerCase().trim();
    filtered = filtered.filter(
      (n) =>
        (n.title || '').toLowerCase().includes(q) ||
        (n.content || '').toLowerCase().includes(q) ||
        (n.studentName || '').toLowerCase().includes(q) ||
        (n.className || '').toLowerCase().includes(q) ||
        (n.subject || '').toLowerCase().includes(q)
    );
  }

  // Sort chronological descending (most recent first)
  filtered.sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
  return filtered;
}

/**
 * Create a new note
 */
export async function createTeacherNote(noteData, teacherId = null) {
  const notes = readStorage(teacherId);
  const now = new Date().toISOString();
  const newNote = {
    id: `note-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    date: noteData.date || now.split('T')[0],
    title: noteData.title?.trim() || 'Untitled Note',
    content: noteData.content || '',
    subject: noteData.subject || 'All Subjects',
    category: noteData.category || 'General',
    classId: noteData.classId || '',
    className: noteData.className || '',
    studentId: noteData.studentId || '',
    studentName: noteData.studentName || '',
    hasReminder: Boolean(noteData.hasReminder),
    reminderDate: noteData.reminderDate || noteData.date || now.split('T')[0],
    reminderTime: noteData.reminderTime || '09:00',
    createdAt: now,
    updatedAt: now,
  };

  const updated = [newNote, ...notes];
  writeStorage(updated, teacherId);
  return newNote;
}

/**
 * Update an existing note
 */
export async function updateTeacherNote(id, patch, teacherId = null) {
  const notes = readStorage(teacherId);
  const index = notes.findIndex((n) => n.id === id);
  if (index === -1) throw new Error(`Note ${id} not found`);

  const updatedNote = {
    ...notes[index],
    ...patch,
    updatedAt: new Date().toISOString(),
  };

  notes[index] = updatedNote;
  writeStorage(notes, teacherId);
  return updatedNote;
}

/**
 * Delete a note
 */
export async function deleteTeacherNote(id, teacherId = null) {
  const notes = readStorage(teacherId);
  const filtered = notes.filter((n) => n.id !== id);
  writeStorage(filtered, teacherId);
  return { success: true };
}
